import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import { customerAPI } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorAlert from '../../components/ErrorAlert';

const ISSUE_OPTIONS = [
  { value: 'general_feedback', label: 'General Feedback' },
  { value: 'feature_request', label: 'Feature Request' },
  { value: 'bug_report', label: 'Bug Report' }
];

const getMessageLabel = (issueType) => {
  if (issueType === 'bug_report') {
    return 'Describe the issue and steps to recreate the issue';
  }
  return 'We welcome your feedback! Please be as detailed as possible';
};

const CustomerFeedback = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    issueType: 'general_feedback',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const { data: settings, isLoading, isError, error } = useQuery(
    ['customer-my-settings'],
    async () => {
      const response = await customerAPI.getMySettings();
      return response.data || response;
    },
    { staleTime: 60000 }
  );

  useEffect(() => {
    if (settings) {
      setFormData((prev) => ({
        ...prev,
        name: settings.name || '',
        email: settings.email || ''
      }));
    }
  }, [settings]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    try {
      await customerAPI.submitFeedback(formData);
      toast.success('Feedback submitted successfully');
      setFormData((prev) => ({ ...prev, issueType: 'general_feedback', message: '' }));
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) return <LoadingSpinner />;
  if (isError) return <ErrorAlert message={error?.message || 'Error loading feedback form'} />;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="bg-white shadow rounded-lg p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-semibold text-brand-navy">Report a Bug or Provide Feedback</h1>
          <p className="mt-1 text-sm text-gray-500">
            Share feedback, request a feature, or tell us about a bug you found.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-brand-gold focus:outline-none focus:ring-brand-gold"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-brand-gold focus:outline-none focus:ring-brand-gold"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Please select an issue:</label>
            <select
              name="issueType"
              value={formData.issueType}
              onChange={handleChange}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-brand-gold focus:outline-none focus:ring-brand-gold"
              required
            >
              {ISSUE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {getMessageLabel(formData.issueType)}
            </label>
            <textarea
              name="message"
              value={formData.message}
              onChange={handleChange}
              rows={8}
              className="w-full rounded-md border border-gray-300 px-3 py-2 focus:border-brand-gold focus:outline-none focus:ring-brand-gold"
              required
            />
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md border border-brand-gold bg-brand-gold px-4 py-2 text-sm font-medium text-white hover:bg-brand-gold/90 disabled:opacity-50"
            >
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerFeedback;
